import {
  TableCell,
  TableRow,
  Box,
  Tooltip,
  Chip,
  Popover,
} from '@mui/material';
import TooltipTableCell from '../../components/formlib/TooltipTableCell';
import { ReactComponent as EditIcon } from '../../assets/images/editvector.svg';
import { ReactComponent as MessageIcon } from '../../assets/images/messageIcon.svg';
import { FC, useContext, useEffect, useState } from 'react';
import {
  Activity,
  Appointment,
  updateAppointmentVisitNote,
} from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { ReactComponent as StatusIcon } from '../../assets/images/statusIcon.svg';
import { ReactComponent as AcceptIcon } from '../../assets/images/acceptIcon.svg';
import { ReactComponent as RejectIcon } from '../../assets/images/rejectIcon.svg';
import { ReactComponent as UndoIcon } from '../../assets/images/undoIcon.svg';
import { ReactComponent as ArchiveIcon } from '../../assets/images/archiveIcon.svg';
import { ReactComponent as ChevronUp } from '../../assets/images/chevronUp.svg';
import { ReactComponent as ChevronDown } from '../../assets/images/chevronDown.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/info.svg';
import TextWithIcon from '../../components/formlib/TextWithIcon';
import ActivityEditModal from '../../components/formlib/modal/ActivityEditModal';
import ActivityNoteModal from '../../components/formlib/modal/ActivityNoteModal';
import { getTimeString } from '../../utils/dateUtil';
import { EmployeeType } from '../../utils/type';
import AppointmentTableRow from './AppointmentTableRow';
import MediumTypography from '../../components/formlib/MediumTypography';
import ActionToolTip from '../../components/formlib/ActionToolTip';
import {
  formatNumberToDecimal,
  formatNumberToDecimalString,
  getStatusColor,
} from '../../utils/utilities';
import ActivityGroupBillingModal from '../../components/formlib/modal/ActivityGroupBillingModal';
import { GroupBilling } from '../../services/progressNotes/ProgressNotesApi';
import moment from 'moment';
import dayjs from 'dayjs';
import { DATE_FORMAT, STORAGE_USERNAME_KEY } from '../../services/Constant';
import { ActivitySheetContext } from '../../context/ActivitySheetContext';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import HoursOutTooltip from './HoursOutTooltip';
import { TimeOffData } from '../../services/myTimeoff/MyTimeoffApi';

export interface UpdateTime {
  totalHoursDec?: number | null;
  lunchTimeIncluded?: boolean | null;
  approvedNonBillableHoursDec?: number | null;
  otherAssocWorkHrsDec?: number | null;
  otherNonBillableHrsDec?: number | null;
  noShowTravelDocDec?: number | null;
  note?: string;
  timeOffList?: TimeOffData[];
  activityDate?: string;
}

interface ActivityProps {
  props: Activity;
  employeeType: EmployeeType | null;
  isEmployee: boolean;
  headerCount: number;
  onStatusUpdate?: (activitySheetId: string | null, status: StatusType) => void;
  updateTimeOrNote?: (
    activitySheetId: string,
    data: UpdateTime,
    updateType: 'NOTE' | 'TIME',
  ) => void;
  updateGroupBilling: (data: GroupBilling) => void;
  archiveItem?: (
    activitySheetId: string,
    progressNotesId: string,
    archive: boolean,
  ) => void;
  screenName: string;
  selectedDate?: Date;
  status: StatusType | null;
}

export type StatusType =
  | 'Submitted'
  | 'In Progress'
  | 'Approved'
  | 'Rejected'
  | 'Pending'
  | 'Open'
  | 'Completed'
  | 'Inactive'
  | 'Active'
  | 'Canceled'
  | 'Not Submitted'
  | 'On-hold';

const ActivityTableRow: FC<ActivityProps> = ({
  props,
  isEmployee,
  headerCount,
  employeeType,
  onStatusUpdate,
  updateTimeOrNote,
  updateGroupBilling,
  archiveItem,
  screenName,
  selectedDate,
  status,
}) => {
  const [state, setState] = useState<{
    status: StatusType;
    openEditModel: boolean;
    openNoteModel: boolean;
    openGroupBillingModel: boolean;
    openVisitModal: boolean;
    visitId: string | null;
    visitDescription: string | null;
    progressNotesId: string | null;
  }>({
    status: 'Submitted',
    openEditModel: false,
    openNoteModel: false,
    openGroupBillingModel: false,
    openVisitModal: false,
    visitId: null,
    visitDescription: null,
    progressNotesId: null,
  });
  const [expand, setExpand] = useState<boolean>(
    !props.archived &&
      ((props.appointments && props.appointments.length > 0) ||
        (props.groupBilling && props.groupBilling.length > 0)),
  );
  const { context } = useContext(ActivitySheetContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const canHideSaveButton = () => {
    if (screenName === 'EmployeeActivity') {
      return props.payrollStatus === 'Approved';
    } else if (screenName === 'ApprovalActivitySheet') {
      if (
        status === 'Not Submitted' ||
        status === 'Rejected' ||
        status === null
      ) {
        return true;
      }
      return false;
    }
    return true;
  };

  const canHideVisitNoteSaveButton = () => {
    if (screenName === 'EmployeeActivity') {
      return state.status === 'Approved' || state.status === 'Submitted';
    } else if (screenName === 'ApprovalActivity') {
      if (
        state.status === 'Not Submitted' ||
        state.status === 'Rejected' ||
        state.status === null
      ) {
        return true;
      }
      return false;
    }
    return true;
  };

  const getAppStartTime = (
    time: string | null,
    format: string,
    utc: boolean,
  ) => {
    if (time === null) {
      return '';
    }
    return getTimeString(time, format, utc);
  };

  const displayPopup = (activity: Activity) => {
    return activity.hoursOutDec !== null && activity.hoursOutDec !== 0;
  };

  useEffect(() => {
    setState((previousState) => ({
      ...previousState,
      status: props.status ?? 'Submitted',
    }));
    return () => {
      setExpand(false);
    };
  }, [props.status]);

  const updateNoteState = () => {
    setState((previousState) => ({
      ...previousState,
      openNoteModel: !previousState.openNoteModel,
    }));
  };
  const updateEditState = () => {
    setState((previousState) => ({
      ...previousState,
      openEditModel: !previousState.openEditModel,
    }));
  };
  const openGroupBillingModal = (id: string | null, index: number) => {
    if (state.openGroupBillingModel) {
      setSelectedAppointment(null);
    } else if (props.groupBilling.length > 0 && id) {
      const groupBilling = props.groupBilling[index];
      const appt = groupBilling.appointments.find(
        (a) => a.appointmentId === id,
      );
      if (appt) {
        appt.groupBillingStartTime = groupBilling.groupBillingStartTime;
        appt.groupBillingDurationDec = groupBilling.groupBillingDurationDec;
        if (appt.groupBillingStartTime) {
          const newAppt = structuredClone(appt);
          newAppt.groupBillingStartTime = moment(
            newAppt.groupBillingStartTime,
            'YYYY-MM-DDTHH:mm',
          )
            .utc(true)
            .local()
            .format();
          setSelectedAppointment(newAppt);
        } else {
          setSelectedAppointment(appt);
        }
      }
    } else {
      setSelectedAppointment(null);
    }
    setState((previousState) => ({
      ...previousState,
      openGroupBillingModel: !previousState.openGroupBillingModel,
    }));
  };

  const getStatusAliasName = (s: StatusType | null) => {
    if (!s) {
      return s;
    }
    if (s === 'Approved') {
      return 'Closed';
    } else if (s === 'Rejected') {
      return 'Open';
    }
    return s;
  };

  const renderAccept = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          marginLeft: '8px',
          pointerEvents:
            status === 'Not Submitted' || status === 'Rejected'
              ? 'none'
              : 'auto',
        }}
      >
        <Box
          sx={{ cursor: 'pointer' }}
          display={'flex'}
          alignItems={'center'}
          onClick={() => {
            setState({ ...state, status: 'Approved' });
            if (onStatusUpdate) {
              onStatusUpdate(props.activitySheetId, 'Approved');
            }
          }}
        >
          <AcceptIcon />
        </Box>
        <Box
          sx={{ cursor: 'pointer', marginLeft: '24px' }}
          display={'flex'}
          alignItems={'center'}
          onClick={() => {
            setState({ ...state, status: 'Rejected' });
            if (onStatusUpdate) {
              onStatusUpdate(props.activitySheetId, 'Rejected');
            }
          }}
        >
          <RejectIcon />
        </Box>
      </Box>
    );
  };
  const renderUndo = () => {
    return (
      <Box sx={{ display: 'flex' }} alignItems={'center'}>
        <TextWithIcon
          LeftIcon={<StatusIcon fill={getStatusColor(state.status)} />}
          label={state.status}
          className="paddingZero archivedIconMinusRemoveSm"
          defaultLabel="Status"
          sxProps={{
            color: getStatusColor(state.status),
            paddingRight: '4px',
            textTransform: 'none',
          }}
        />
        {props.originalStatus !== 'Not Submitted' &&
          props.originalStatus !== 'Approved' &&
          props.status !== 'Rejected' && (
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                setState({ ...state, status: 'Submitted' });
                if (onStatusUpdate) {
                  onStatusUpdate(props.activitySheetId, 'Submitted');
                }
              }}
            >
              <UndoIcon />
            </Box>
          )}
        {(status === 'Approved' || status === 'Pending') &&
          props.status === 'Rejected' && (
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                setState({ ...state, status: 'Submitted' });
                if (onStatusUpdate) {
                  onStatusUpdate(props.activitySheetId, 'Submitted');
                }
              }}
            >
              <UndoIcon />
            </Box>
          )}
      </Box>
    );
  };

  const getWeekendBackgroundColor = () => {
    if (dayjs(props.day).day() === 0 || dayjs(props.day).day() === 6) {
      return 'var(--thom-green-primary2-bg)';
    }
  };

  const getBackgroundColor = () => {
    if (props.isHoliday) {
      return '#FFEBDD';
    } else if (props.archived) {
      return '#97A6A5';
    } else if (props.rowId === 0) {
      return '#ffffff';
    } else {
      return '#ECF9F8';
    }
  };

  const checkEditingAllowed = () => {
    if (!isEmployee) {
      return false;
    }
    return (
      props.status === null ||
      props.status === 'Not Submitted' ||
      props.status === 'Rejected'
    );
  };

  const updateVisitLevelNote = async (description: string) => {
    const { visitId, progressNotesId } = state;
    if (visitId && progressNotesId !== null) {
      toggleLoader(true);
      updateAppointmentVisitNote({
        activitySheetId: props.activitySheetId,
        visitId: visitId,
        description,
      })
        .then(() => {
          if (context) {
            context.updateVisitLevelNote(progressNotesId, visitId, description);
          }
          setState((previousState) => ({
            ...previousState,
            openVisitModal: false,
            description: null,
            progressNotesId: null,
            visitId: null,
          }));
          if (context) {
            context.updateNoteErrorMessage(
              'success',
              'visitNoteUpdateSuccessMessage',
              'Visit note updated successfully',
            );
          }
          toggleLoader(false);
        })
        .catch((error) => {
          if (context) {
            if (isCustomError(error)) {
              const apiError = error as ApiError;
              context.updateNoteErrorMessage(
                'error',
                apiError.id,
                apiError.message,
              );
            } else {
              context.updateNoteErrorMessage(
                'error',
                'visitNoteUpdateFailureMessage',
                'Failed to update visit note',
              );
            }
          }
          toggleLoader(false);
        });
    }
  };

  const editable = (payrollStatus: StatusType | null) => {
    if (
      payrollStatus === null ||
      payrollStatus === 'Not Submitted' ||
      payrollStatus === 'Rejected'
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      {state.openGroupBillingModel && (
        <ActivityGroupBillingModal
          editingAllowed={checkEditingAllowed()}
          open={state.openGroupBillingModel}
          handleClose={() => {
            openGroupBillingModal(null, -1);
          }}
          onGroupBillingEdit={(values) => {
            if (selectedAppointment === null) {
              return;
            }
            const date = moment(
              selectedAppointment.startTime,
              'YYYY-MM-DDTHH:mm',
            )
              .utc(true)
              .local()
              .format(DATE_FORMAT);
            const data: GroupBilling = {
              appointmentId: selectedAppointment.appointmentId,
              attendeeId: selectedAppointment.attendeeId,
              groupBillingDurationDec:
                values.duration === null ? null : values.duration,
              groupBillingStartTime:
                values.startTime === null
                  ? null
                  : moment(
                      `${date} ${dayjs(
                        values.startTime,
                        'YYYY-MM-DDTHH:mm',
                      ).format('HH:mm')}`,
                      'MM/DD/YYYY HH:mm',
                    )
                      .utc()
                      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
            };
            openGroupBillingModal(null, -1);
            updateGroupBilling(data);
          }}
          startTime={
            selectedAppointment === null
              ? null
              : selectedAppointment.groupBillingStartTime
          }
          duration={
            selectedAppointment === null
              ? null
              : selectedAppointment.groupBillingDurationDec
          }
        />
      )}
      {state.openEditModel && (
        <ActivityEditModal
          selectedDate={props.day ?? ''}
          employeeType={employeeType}
          employeeName={localStorage.getItem(STORAGE_USERNAME_KEY) ?? ''}
          employeeId={null}
          canEdit={editable(props.payrollStatus)}
          open={state.openEditModel}
          totalHoursDec={props.totalHoursDec}
          lunchTimeIncluded={props.lunchTimeIncluded}
          approvedNonBillableHoursDec={props.approvedNonBillableHoursDec}
          otherAssocWorkHrsDec={props.otherAssocWorkHrsDec}
          otherNonBillableHrsDec={props.otherNonBillableHrsDec}
          noShowTravelDocDec={props.noShowTravelDocDec}
          handleClose={updateEditState}
          onTimeEdit={(values) => {
            if (updateTimeOrNote && props.activitySheetId !== null) {
              updateEditState();
              updateTimeOrNote(
                props.activitySheetId,
                {
                  totalHoursDec: values.totalHoursDec,
                  lunchTimeIncluded: values.lunchTimeIncluded,
                  approvedNonBillableHoursDec:
                    values.approvedNonBillableHoursDec,
                  otherAssocWorkHrsDec: values.otherAssocWorkHrsDec,
                  otherNonBillableHrsDec: values.otherNonBillableHrsDec,
                  noShowTravelDocDec: values.noShowTravelDocDec,
                  note: props.description ?? '',
                  activityDate: dayjs
                    .utc(values.activityDate)
                    .startOf('day')
                    .format('MM/DD/YYYY'),
                },
                'TIME',
              );
            }
          }}
        />
      )}

      {/* This modal is to update date level note */}
      {state.openNoteModel && (
        <ActivityNoteModal
          open={state.openNoteModel}
          note={props.description ?? ''}
          handleClose={updateNoteState}
          maxLength={2000}
          hideUpdateButton={canHideSaveButton()}
          updateNote={(note: string) => {
            if (updateTimeOrNote && props.activitySheetId !== null) {
              updateNoteState();
              updateTimeOrNote(
                props.activitySheetId,
                {
                  totalHoursDec: props.totalHoursDec,
                  approvedNonBillableHoursDec:
                    props.approvedNonBillableHoursDec,
                  note: note.trim(),
                  activityDate: dayjs(props.day).format('MM/DD/YYYY'),
                },
                'NOTE',
              );
            }
          }}
        />
      )}

      {/* This modal is to update visit level note */}
      {state.openVisitModal && (
        <ActivityNoteModal
          open={state.openVisitModal}
          note={state.visitDescription ?? ''}
          handleClose={() => {
            setState((previousState) => ({
              ...previousState,
              progressNotesId: null,
              visitId: null,
              openVisitModal: !previousState.openVisitModal,
            }));
          }}
          maxLength={2000}
          hideUpdateButton={canHideVisitNoteSaveButton()}
          updateNote={(note: string) => {
            updateVisitLevelNote(note.trim());
          }}
        />
      )}

      <TableRow
        key={props.appointmentId}
        sx={{
          backgroundColor: getWeekendBackgroundColor(),
          opacity: props.archived ? '0.7' : '1',
        }}
      >
        <TableCell sx={{ borderBottom: expand ? 'unset' : '' }}>
          <Box display={'flex'} alignItems={'center'}>
            {((props.appointments && props.appointments.length > 0) ||
              (props.groupBilling && props.groupBilling.length > 0)) &&
            !props.archived ? (
              <Box
                sx={{ paddingLeft: '8px', cursor: 'pointer' }}
                onClick={() => {
                  setExpand(!expand);
                }}
              >
                {expand && <ChevronUp />}
                {!expand && <ChevronDown />}
              </Box>
            ) : (
              <Box sx={{ paddingLeft: '22px' }} />
            )}
            <Box>
              {props.holidayTitle !== null && (
                <Box>
                  <Tooltip title={props.holidayTitle}>
                    <Chip
                      size="small"
                      label={props.holidayTitle}
                      sx={{
                        position: 'relative',
                        fontFamily: 'Lato-Regular',
                        fontSize: '10px',
                        color: 'white',
                        marginLeft: '8px',
                        backgroundColor: '#F96300',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '150px',
                      }}
                    />
                  </Tooltip>
                </Box>
              )}
              <MediumTypography
                sxProps={{
                  paddingLeft: '8px',
                  color:
                    props.isHoliday !== null && props.isHoliday
                      ? '#F96300'
                      : '#2A4241',
                }}
                className="singleLine"
                label={getAppStartTime(props.day, 'dddd, MMM DD', true)}
              />
            </Box>
          </Box>
        </TableCell>
        <TooltipTableCell
          tooltipSxProps={{
            borderBottom: expand ? 'unset' : '',
          }}
          value={
            props.billableHoursDec === null
              ? '-'
              : formatNumberToDecimal(props.billableHoursDec)
          }
          visible={false}
        />
        {employeeType !== 'Salaried Exempt' && (
          <TooltipTableCell
            tooltipSxProps={{
              borderBottom: expand ? 'unset' : '',
            }}
            value={
              props.otherAssocWorkHrsDec === null
                ? '-'
                : formatNumberToDecimal(props.otherAssocWorkHrsDec)
            }
            visible={false}
          />
        )}

        <TooltipTableCell
          tooltipSxProps={{
            borderBottom: expand ? 'unset' : '',
          }}
          value={
            props.approvedNonBillableHoursDec === null
              ? '-'
              : formatNumberToDecimal(props.approvedNonBillableHoursDec)
          }
          visible={false}
        />

        {employeeType === 'Hourly Non-Exempt' && (
          <TooltipTableCell
            tooltipSxProps={{
              borderBottom: expand ? 'unset' : '',
            }}
            value={
              props.noShowTravelDocDec === null
                ? '-'
                : formatNumberToDecimal(props.noShowTravelDocDec)
            }
            visible={false}
          />
        )}
        <TooltipTableCell
          tooltipSxProps={{
            borderBottom: expand ? 'unset' : '',
          }}
          value={
            props.otherNonBillableHrsDec === null
              ? '-'
              : formatNumberToDecimal(props.otherNonBillableHrsDec)
          }
          visible={false}
        />
        <TableCell sx={{ borderBottom: expand ? 'unset' : '' }}>
          <Box display={'flex'} alignItems={'center'}>
            <MediumTypography
              label={
                props.hoursOutDec === null
                  ? '-'
                  : formatNumberToDecimalString(props.hoursOutDec)
              }
            />
            {displayPopup(props) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px',
                  cursor: 'pointer',
                }}
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
              >
                <InfoIcon stroke="var(--thom-mono-gray)" />
                <Popover
                  id="mouse-over-popover"
                  sx={{
                    pointerEvents: 'none',
                  }}
                  open={open}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  onClose={handlePopoverClose}
                  disableRestoreFocus
                >
                  <HoursOutTooltip activity={props} />
                </Popover>
              </Box>
            )}
          </Box>
        </TableCell>
        {employeeType !== 'Salaried Exempt' && (
          <TooltipTableCell
            tooltipSxProps={{
              borderBottom: expand ? 'unset' : '',
            }}
            value={props.lunchTimeIncluded ? 'Yes' : 'No'}
            visible={false}
          />
        )}
        <TooltipTableCell
          tooltipSxProps={{
            borderBottom: expand ? 'unset' : '',
          }}
          value={
            props.totalHoursDec === null
              ? '-'
              : formatNumberToDecimal(props.totalHoursDec)
          }
          visible={false}
        />
        {screenName === 'EmployeeActivity' ? (
          <TableCell
            sx={{
              borderBottom: expand ? 'unset' : '',
              display:
                screenName === 'EmployeeActivity' ||
                screenName === 'ApprovalActivitySheet'
                  ? null
                  : 'none',
            }}
          >
            <Box sx={{ display: 'flex' }} alignItems={'center'}>
              <StatusIcon fill={getStatusColor(props.status)} />
              <MediumTypography
                className="singleLine"
                sxProps={{ paddingLeft: '8px' }}
                label={getStatusAliasName(props.status) ?? '-'}
              />
            </Box>
          </TableCell>
        ) : (
          <TableCell
            padding={'none'}
            sx={{
              borderBottom: expand ? 'unset' : '',
              display:
                screenName === 'EmployeeActivity' ||
                screenName === 'ApprovalActivitySheet'
                  ? 'auto'
                  : 'none',
            }}
          >
            <Box>
              {state.status === 'Submitted' ? renderAccept() : null}
              {status !== null && state.status !== 'Submitted' && renderUndo()}
            </Box>
          </TableCell>
        )}

        <TableCell
          sx={{
            borderBottom: expand ? 'unset' : '',
            display:
              screenName === 'EmployeeActivity' ||
              screenName === 'ApprovalActivitySheet'
                ? null
                : 'none',
          }}
        >
          {props.archived ? (
            <Box
              display={'inline-block'}
              sx={{
                backgroundColor: '#fff',
                borderRadius: '4px',
                cursor: 'pointer',
                paddingLeft: '8px',
                paddingRight: '4px',
              }}
              onClick={() => {
                if (
                  props.archivalStatus !== null &&
                  props.archivalStatus !== 'Submitted' &&
                  archiveItem &&
                  props.activitySheetId !== null
                ) {
                  archiveItem(props.activitySheetId, '', !props.archived);
                }
              }}
            >
              <TextWithIcon
                LeftIcon={
                  props.archivalStatus !== null &&
                  props.archivalStatus !== 'Submitted' && <UndoIcon />
                }
                className="paddingZero archivedIconMinusRemoveSm"
                labelId={'archivedText'}
                defaultLabel="Archived"
                sxProps={{
                  color: '#97A6A5',
                  textTransform: 'none',
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <ActionToolTip labelId="BlockTime.note" defaultLabel="Note">
                <Box
                  sx={{ padding: '4px', display: 'flex', cursor: 'pointer' }}
                  onClick={updateNoteState}
                >
                  <MessageIcon
                    stroke={props.description ? '#00C6B8' : '#97A6A5'}
                  />
                </Box>
              </ActionToolTip>
              {screenName !== 'ApprovalActivitySheet' &&
                props.payrollStatus !== 'Approved' &&
                props.payrollStatus !== 'Submitted' && (
                  <>
                    <ActionToolTip labelId="editText" defaultLabel="Edit">
                      <Box
                        sx={{
                          padding: '4px',
                          display: 'flex',
                          cursor: 'pointer',
                          marginLeft: '8px',
                        }}
                        onClick={updateEditState}
                      >
                        <EditIcon />
                      </Box>
                    </ActionToolTip>
                    <ActionToolTip labelId="archiveText" defaultLabel="Archive">
                      <Box
                        sx={{
                          padding: '4px',
                          display: 'none',
                          cursor: 'pointer',
                          marginLeft: '8px',
                        }}
                        onClick={() => {
                          if (
                            props.archivalStatus === null ||
                            props.archivalStatus === 'Pending'
                          ) {
                            if (archiveItem && props.activitySheetId !== null) {
                              if (expand) {
                                setExpand(!expand);
                              }
                              archiveItem(
                                props.activitySheetId,
                                '',
                                !props.archived,
                              );
                            }
                          }
                        }}
                      >
                        <ArchiveIcon />
                      </Box>
                    </ActionToolTip>
                  </>
                )}
              {screenName === 'ApprovalActivitySheet' &&
                props.originalStatus === 'Approved' &&
                props.status !== 'Submitted' &&
                props.status !== 'Rejected' && (
                  <ActionToolTip labelId="revertText" defaultLabel="Revert">
                    <Box
                      sx={{
                        display: 'flex',
                        pointerEvents: status === 'Rejected' ? 'none' : 'auto',
                        cursor: status === 'Rejected' ? 'none' : 'pointer',
                        marginLeft: '12px',
                        marginTop: '5px',
                      }}
                      onClick={() => {
                        setState({ ...state, status: 'Submitted' });
                        if (onStatusUpdate) {
                          onStatusUpdate(props.activitySheetId, 'Submitted');
                        }
                      }}
                    >
                      <UndoIcon stroke={'var(--thom-radical-red)'} />
                    </Box>
                  </ActionToolTip>
                )}
            </Box>
          )}
        </TableCell>
      </TableRow>
      {expand === true && (
        <TableRow
          sx={{
            backgroundColor: getBackgroundColor(),
          }}
        >
          <TableCell
            colSpan={headerCount}
            sx={{ paddingLeft: '24px !important' }}
          >
            <AppointmentTableRow
              rowId={props.rowId}
              groupAppointments={props.groupBilling}
              parentArchived={props.archived}
              isHoliday={props.isHoliday ?? false}
              parentStatus={props.status}
              isEmployee={isEmployee}
              archiveAppointment={(sheetId, archived) => {
                if (archiveItem) {
                  archiveItem(props.activitySheetId, sheetId, archived);
                }
              }}
              updateVisitNote={(
                progressNoteId,
                visitId,
                description,
                visitStatus,
              ) => {
                setState((previousState) => ({
                  ...previousState,
                  progressNotesId: progressNoteId,
                  visitId: visitId,
                  openVisitModal: !previousState.openVisitModal,
                  visitDescription: description,
                  status: visitStatus ?? 'Not Submitted',
                }));
              }}
              screenName={screenName}
              selectedDate={selectedDate}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ActivityTableRow;
